import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { SF_MISMATCH_WARN_M } from '../../utils/sfAlignment';
import {
  buildSplitTimeGroups,
  computeSplitTimeStats,
  pruneSplitDisabledKeys,
} from '../../utils/splitTimesModel';
import {
  exportSplitTimesPng,
  SPLIT_TIMES_STAT_COLORS,
} from '../../utils/splitTimesExport';
import styles from './SectorTimes.module.css';

function formatSectorTime(seconds) {
  if (seconds == null) return '—';
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = (seconds - m * 60).toFixed(3).padStart(6, '0');
    return `${m}:${s}`;
  }
  return seconds.toFixed(3);
}

function formatLapTime(seconds, str) {
  if (str) return str;
  if (seconds == null || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = (seconds - m * 60).toFixed(3).padStart(6, '0');
  return `${m}:${s}`;
}

function groupMeta(group) {
  return [group.date, group.sessionLabel, group.vehicle].filter(Boolean).join(' · ');
}

export default function SectorTimes({ datasets = [], sectors = [], trackName }) {
  const [disabledKeys, setDisabledKeys] = useState(() => new Set());
  const panelRef = useRef(null);

  const groups = useMemo(
    () => buildSplitTimeGroups(datasets, SF_MISMATCH_WARN_M),
    [datasets],
  );
  const allRows = useMemo(() => groups.flatMap(group => group.laps.map(lap => ({
    ...lap,
    groupKey: group.key,
    groupMismatch: group.mismatch,
  }))), [groups]);
  const sessionSignature = datasets.map(dataset => dataset.sessionId).join('|');

  // Preserve checkbox choices through sector refreshes and comparison loading,
  // but discard choices belonging to a removed/replaced session.
  useEffect(() => {
    setDisabledKeys(previous => {
      const next = pruneSplitDisabledKeys(previous, datasets.map(dataset => dataset.sessionId));
      if (next.size === previous.size && [...next].every(key => previous.has(key))) return previous;
      return next;
    });
    // sessionSignature captures membership without retriggering on value/loading refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionSignature]);

  const eligibleRows = useMemo(
    () => allRows.filter(row => !row.groupMismatch),
    [allRows],
  );
  const activeRows = useMemo(
    () => eligibleRows.filter(row => !disabledKeys.has(row.rowKey)),
    [eligibleRows, disabledKeys],
  );
  const stats = useMemo(
    () => computeSplitTimeStats(activeRows, sectors.length),
    [activeRows, sectors.length],
  );

  const isEnabled = row => !row.groupMismatch && !disabledKeys.has(row.rowKey);
  const toggleRows = rows => {
    const eligible = rows.filter(row => !row.groupMismatch);
    if (!eligible.length) return;
    setDisabledKeys(previous => {
      const next = new Set(previous);
      const allEnabled = eligible.every(row => !previous.has(row.rowKey));
      eligible.forEach(row => {
        if (allEnabled) next.add(row.rowKey);
        else next.delete(row.rowKey);
      });
      return next;
    });
  };
  const toggleLap = row => {
    if (row.groupMismatch) return;
    setDisabledKeys(previous => {
      const next = new Set(previous);
      if (next.has(row.rowKey)) next.delete(row.rowKey);
      else next.add(row.rowKey);
      return next;
    });
  };

  const globalAllOn = eligibleRows.length > 0
    && eligibleRows.every(row => !disabledKeys.has(row.rowKey));
  const statRows = [
    { label: 'Best', sectorTimes: stats.best, total: stats.bestTotal, ...SPLIT_TIMES_STAT_COLORS.best },
    { label: 'Avg', sectorTimes: stats.avg, total: stats.avgTotal, ...SPLIT_TIMES_STAT_COLORS.avg },
    { label: 'Max', sectorTimes: stats.max, total: stats.maxTotal, ...SPLIT_TIMES_STAT_COLORS.max },
  ];

  const handleExportPng = () => {
    if (!allRows.length) return;
    const slug = value => String(value || '')
      .replace(/[^\w-]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const host = window.location.hostname;
    exportSplitTimesPng({
      title: trackName || 'Split Times',
      subtitle: [
        'Split Times',
        `${groups.length} session${groups.length === 1 ? '' : 's'}`,
        `${activeRows.length}/${allRows.length} laps enabled`,
        `${sectors.length} sectors`,
      ].join(' · '),
      sectors,
      groups: groups.map(group => ({
        label: group.driverName || 'Driver',
        meta: groupMeta(group),
        accent: group.accent,
        mismatch: group.mismatch,
        loading: group.loading,
        error: group.error,
        rows: group.laps.map(lap => ({
          label: `L${lap.displayNumber}`,
          enabled: !group.mismatch && !disabledKeys.has(lap.rowKey),
          sectorTimes: lap.sector_times,
          totalLabel: formatLapTime(lap.lap_time_seconds, lap.lap_time_str),
        })),
      })),
      stats: statRows,
      themeFrom: panelRef.current,
      watermark: /^(localhost|127\.|\d+\.\d+\.)/.test(host) ? 'TrackDay Analysis' : host,
      filename: `${slug(trackName) || 'track'}_split_times.png`,
    }).catch(error => console.error('Split Times export failed:', error));
  };

  const hasStatus = groups.some(group => group.loading || group.error);
  const hasTable = sectors.length > 0 && (allRows.length > 0 || hasStatus);
  const multiSession = groups.length > 1;

  return (
    <div className={styles.panel} ref={panelRef}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>Split Times</span>}>
        <span className={styles.hint}>
          {sectors.length} sector{sectors.length !== 1 ? 's' : ''} · {activeRows.length}/{allRows.length} laps
          {multiSession ? ` · ${groups.length} sessions` : ''}
        </span>
        <button
          type="button"
          className={styles.optBtn}
          onClick={handleExportPng}
          disabled={!allRows.length}
          title="Download the complete split-times table as a PNG image"
        >PNG ⤓</button>
        <HelpButton panel="sectortimes" page="sessions" />
      </PanelHeader>

      {!hasTable ? (
        <div className={styles.empty}>
          {groups.some(group => group.loading) ? 'Loading split times…' : 'Load a session to see split times'}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className={styles.check}
                    checked={globalAllOn}
                    onChange={() => toggleRows(eligibleRows)}
                    disabled={!eligibleRows.length}
                    title={globalAllOn ? 'Deselect all eligible laps' : 'Select all eligible laps'}
                  />
                  Lap
                </th>
                {sectors.map(sector => (
                  <th key={sector.id} title={sector.label}>{sector.label.replace('Sector ', 'S')}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(group => {
                const groupRows = group.laps.map(lap => ({
                  ...lap,
                  groupMismatch: group.mismatch,
                }));
                const groupAllOn = groupRows.length > 0 && !group.mismatch
                  && groupRows.every(row => !disabledKeys.has(row.rowKey));
                const showGroupHeader = multiSession || group.mismatch;
                return (
                  <Fragment key={group.key}>
                    {showGroupHeader && (
                      <tr className={styles.groupRow}>
                        <td className={styles.groupCell} colSpan={sectors.length + 2}>
                          <div className={styles.groupHeader}>
                            <input
                              type="checkbox"
                              className={styles.check}
                              checked={groupAllOn}
                              onChange={() => toggleRows(groupRows)}
                              disabled={group.mismatch || !groupRows.length}
                              title={group.mismatch
                                ? 'Re-align this session start/finish to include its splits'
                                : groupAllOn ? 'Deselect this session' : 'Select this session'}
                            />
                            <span className={styles.groupAccent} style={{ backgroundColor: group.accent }} />
                            <span className={styles.groupName}>{group.driverName || 'Driver'}</span>
                            {groupMeta(group) && <span className={styles.groupMeta}>{groupMeta(group)}</span>}
                            {group.mismatch && (
                              <span className={styles.groupWarning}>
                                S/F mismatch · excluded until re-aligned
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    {group.loading && (
                      <tr><td className={styles.statusCell} colSpan={sectors.length + 2}>Loading split times…</td></tr>
                    )}
                    {!group.loading && group.error && (
                      <tr><td className={styles.errorCell} colSpan={sectors.length + 2}>{group.error}</td></tr>
                    )}
                    {!group.loading && !group.error && groupRows.map(row => {
                      const on = isEnabled(row);
                      return (
                        <tr
                          key={row.rowKey}
                          className={`${group.mismatch ? styles.mismatch : ''} ${!on ? styles.disabled : ''}`}
                          onClick={() => toggleLap(row)}
                          style={{ cursor: group.mismatch ? 'default' : 'pointer' }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className={styles.check}
                              checked={on}
                              disabled={group.mismatch}
                              onChange={() => toggleLap(row)}
                              onClick={event => event.stopPropagation()}
                              title={group.mismatch ? 'Excluded until start/finish is re-aligned' : undefined}
                            />
                            L{row.displayNumber}
                          </td>
                          {row.sector_times.map((value, index) => {
                            const isBest = on && value != null && stats.best[index] != null
                              && Math.abs(value - stats.best[index]) < 0.001;
                            return (
                              <td key={index} className={isBest ? styles.best : ''}>
                                {formatSectorTime(value)}
                              </td>
                            );
                          })}
                          <td>{formatLapTime(row.lap_time_seconds, row.lap_time_str)}</td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className={styles.tbRow}>
                <td>Best</td>
                {stats.best.map((value, index) => <td key={index}>{formatSectorTime(value)}</td>)}
                <td>{formatLapTime(stats.bestTotal, null)}</td>
              </tr>
              <tr className={styles.avgRow}>
                <td>Avg</td>
                {stats.avg.map((value, index) => <td key={index}>{formatSectorTime(value)}</td>)}
                <td>{formatLapTime(stats.avgTotal, null)}</td>
              </tr>
              <tr className={styles.maxRow}>
                <td>Max</td>
                {stats.max.map((value, index) => <td key={index}>{formatSectorTime(value)}</td>)}
                <td>{formatLapTime(stats.maxTotal, null)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
